import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

const PING_MS = 25000;
const MAX_BACKOFF_MS = 30000;

function socketUrl() {
  const base = /^https?:/.test(API) ? API : `${window.location.origin}${API}`;
  return `${base.replace(/^http/, "ws")}/coaching/ws`;
}

/**
 * Staff realtime socket (routes/realtime.py): events for every member chat at the active gym.
 * Authenticated by the login cookie. Reconnects with backoff; `key` (the active gym) reopens it
 * when staff switch gyms, since the server subscribes the socket to one gym.
 */
export function useStaffSocket(key, onEvent) {
  const [connected, setConnected] = useState(false);
  const ws = useRef(null);
  const handler = useRef(onEvent);
  useEffect(() => { handler.current = onEvent; }, [onEvent]);

  useEffect(() => {
    let stop = false;
    let retry;
    let ping;
    let backoff = 1000;
    const open = () => {
      if (stop) return;
      const sock = new WebSocket(socketUrl());
      ws.current = sock;
      sock.onopen = () => {
        backoff = 1000;
        setConnected(true);
        ping = setInterval(() => sock.readyState === 1 && sock.send('{"type":"ping"}'), PING_MS);
      };
      sock.onmessage = (m) => {
        try { handler.current(JSON.parse(m.data)); } catch { /* ignore malformed */ }
      };
      sock.onclose = (e) => {
        clearInterval(ping);
        setConnected(false);
        if (stop || e.code === 4401) return;
        retry = setTimeout(open, backoff);
        backoff = Math.min(MAX_BACKOFF_MS, backoff * 2);
      };
    };
    open();
    return () => {
      stop = true;
      clearTimeout(retry);
      clearInterval(ping);
      ws.current?.close();
    };
  }, [key]);

  const last = useRef(0);
  const sendTyping = useCallback((threadId) => {
    const now = Date.now();
    if (now - last.current < 3000 || ws.current?.readyState !== 1) return;
    last.current = now;
    ws.current.send(JSON.stringify({ type: "typing", thread_id: threadId }));
  }, []);

  return { connected, sendTyping };
}
